import { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LanguageContext";
import { api, formatApiErrorDetail } from "../lib/api";
import { Plus, LogOut, Pencil, Trash2, Mail, Bike } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
    name: "",
    condition: "new",
    category: "Hyper Naked",
    year: new Date().getFullYear(),
    price: 0,
    mileage: 0,
    color: "",
    licence_class: "A",
    bike_type: "motorcycle",
    short_desc_es: "",
    short_desc_en: "",
    description_es: "",
    description_en: "",
    images: [""],
    video_url: "",
    specs: { engine: "", power: "", torque: "", weight: "", transmission: "", fuel_capacity: "", seat_height: "" },
    featured: false,
};

export default function AdminDashboard() {
    const { logout, authHeaders } = useAuth();
    const { t } = useLang();
    const [tab, setTab] = useState("bikes");
    const [bikes, setBikes] = useState([]);
    const [leads, setLeads] = useState([]);
    const [editing, setEditing] = useState(null); // null = no form, {} = create, bike = edit
    const [form, setForm] = useState(EMPTY);
    const [busy, setBusy] = useState(false);

    const loadBikes = () => api.get("/bikes").then((r) => setBikes(r.data || []));
    const loadLeads = () =>
        api.get("/admin/leads", { headers: authHeaders() }).then((r) => setLeads(r.data || []));

    useEffect(() => {
        loadBikes();
        loadLeads();
    }, []);

    const startCreate = () => {
        setEditing({});
        setForm(EMPTY);
    };
    const startEdit = (b) => {
        setEditing(b);
        setForm({
            ...EMPTY,
            ...b,
            images: b.images?.length ? b.images : [""],
            specs: { ...EMPTY.specs, ...(b.specs || {}) },
        });
    };

    const save = async () => {
        setBusy(true);
        try {
            const payload = {
                ...form,
                price: Number(form.price),
                year: Number(form.year),
                mileage: Number(form.mileage || 0),
                images: form.images.filter((u) => u.trim()),
            };
            if (editing && editing.id) {
                await api.put(`/bikes/${editing.id}`, payload, { headers: authHeaders() });
                toast.success(t.admin.updated);
            } else {
                await api.post("/bikes", payload, { headers: authHeaders() });
                toast.success(t.admin.created);
            }
            setEditing(null);
            await loadBikes();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setBusy(false);
        }
    };

    const remove = async (b) => {
        if (!window.confirm(t.admin.delete_confirm)) return;
        try {
            await api.delete(`/bikes/${b.id}`, { headers: authHeaders() });
            toast.success(t.admin.deleted);
            await loadBikes();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        }
    };

    return (
        <div className="mx-auto max-w-7xl px-6 py-12 lg:px-10" data-testid="admin-dashboard">
            <header className="flex flex-wrap items-center justify-between gap-4 border-b border-black/10 pb-6">
                <div>
                    <p className="text-overline">LS39 Admin</p>
                    <h1 className="mt-2 font-display text-3xl font-bold">{t.admin.dashboard}</h1>
                </div>
                <button onClick={logout} className="btn-outline" data-testid="logout-btn">
                    <LogOut size={14} />
                    {t.admin.logout}
                </button>
            </header>

            <div className="mt-6 flex gap-2">
                <TabBtn active={tab === "bikes"} onClick={() => setTab("bikes")} testid="admin-tab-bikes">
                    <Bike size={14} /> {t.admin.bikes} ({bikes.length})
                </TabBtn>
                <TabBtn active={tab === "leads"} onClick={() => setTab("leads")} testid="admin-tab-leads">
                    <Mail size={14} /> {t.admin.leads} ({leads.length})
                </TabBtn>
            </div>

            {tab === "bikes" && (
                <div className="mt-8">
                    <button onClick={startCreate} className="btn-primary" data-testid="add-bike-btn">
                        <Plus size={14} /> {t.admin.add_bike}
                    </button>

                    <div className="mt-8 border border-black/10">
                        <div className="grid grid-cols-12 gap-4 border-b border-black/10 px-4 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                            <div className="col-span-4">Name</div>
                            <div className="col-span-2">Condition</div>
                            <div className="col-span-2">Cat</div>
                            <div className="col-span-2">Price</div>
                            <div className="col-span-2 text-right">Actions</div>
                        </div>
                        {bikes.map((b) => (
                            <div
                                key={b.id}
                                className="grid grid-cols-12 gap-4 border-b border-black/5 px-4 py-4 text-sm hover:bg-black/5"
                                data-testid={`admin-bike-row-${b.id}`}
                            >
                                <div className="col-span-4 font-display font-semibold">{b.name}</div>
                                <div className="col-span-2 font-mono text-xs uppercase">{b.condition}</div>
                                <div className="col-span-2 text-neutral-500">{b.category}</div>
                                <div className="col-span-2 font-mono" style={{ color: "var(--ls-terracotta)" }}>
                                    €{Number(b.price).toLocaleString("es-ES")}
                                </div>
                                <div className="col-span-2 flex justify-end gap-2">
                                    <button onClick={() => startEdit(b)} className="border border-black/15 p-2 hover:border-black/30" data-testid={`edit-bike-${b.id}`}>
                                        <Pencil size={14} />
                                    </button>
                                    <button onClick={() => remove(b)} className="border border-black/15 p-2 hover:border-red-500/60" data-testid={`del-bike-${b.id}`}>
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {tab === "leads" && (
                <div className="mt-8 space-y-4" data-testid="leads-list">
                    {leads.length === 0 && <p className="text-neutral-500 font-mono text-sm">{t.admin.no_leads}</p>}
                    {leads.map((l) => (
                        <div key={l.id} className="border border-black/10 p-5" data-testid={`lead-${l.id}`}>
                            <div className="flex flex-wrap items-center justify-between gap-3">
                                <span
                                    className="font-mono text-[10px] uppercase tracking-[0.25em] px-3 py-1"
                                    style={{
                                        background: l.type === "service" ? "var(--ls-blue)" : l.type === "test_ride" ? "var(--ls-terracotta)" : "rgba(255,255,255,0.08)",
                                    }}
                                >
                                    {l.type}
                                </span>
                                <span className="font-mono text-xs text-neutral-500">{new Date(l.created_at).toLocaleString()}</span>
                            </div>
                            <p className="mt-3 font-display text-lg">{l.name} · <span className="text-neutral-500 text-sm font-normal">{l.email}</span></p>
                            {l.phone && <p className="text-sm text-neutral-500">{l.phone}</p>}
                            {l.message && <p className="mt-2 text-sm text-neutral-600">{l.message}</p>}
                            {l.bike_name && <p className="mt-2 text-sm text-neutral-600">Bike: {l.bike_name}</p>}
                            {l.motorcycle_model && <p className="mt-2 text-sm text-neutral-600">Bike: {l.motorcycle_model} · {l.service_type}</p>}
                            {l.preferred_date && <p className="mt-1 font-mono text-xs text-neutral-500">{l.preferred_date} {l.preferred_time || ""}</p>}
                            {l.notes && <p className="mt-2 text-sm text-neutral-500 italic">"{l.notes}"</p>}
                        </div>
                    ))}
                </div>
            )}

            {/* Editor */}
            {editing !== null && (
                <div className="fixed inset-0 z-50 bg-white/90 backdrop-blur-md flex items-start justify-center overflow-auto p-4" data-testid="bike-editor">
                    <div className="glass-strong w-full max-w-3xl my-8 p-8">
                        <h2 className="font-display text-2xl font-bold">
                            {editing.id ? t.admin.edit_bike : t.admin.add_bike}
                        </h2>
                        <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
                            <In label="Name" v={form.name} on={(v) => setForm({ ...form, name: v })} testid="ed-name" />
                            <Sel label="Condition" v={form.condition} on={(v) => setForm({ ...form, condition: v })} opts={["new", "used"]} testid="ed-cond" />
                            <Sel
                                label="Category"
                                v={form.category}
                                on={(v) => setForm({ ...form, category: v })}
                                opts={["Hyper Naked", "Supersport", "Adventure", "Sport Touring", "Sport Heritage", "Scooter"]}
                                testid="ed-cat"
                            />
                            <Sel
                                label="Licence class"
                                v={form.licence_class}
                                on={(v) => setForm({ ...form, licence_class: v })}
                                opts={["A1", "A2", "A"]}
                                testid="ed-licence"
                            />
                            <Sel
                                label="Bike type"
                                v={form.bike_type}
                                on={(v) => setForm({ ...form, bike_type: v })}
                                opts={["motorcycle", "scooter"]}
                                testid="ed-biketype"
                            />
                            <In label="Year" type="number" v={form.year} on={(v) => setForm({ ...form, year: v })} testid="ed-year" />
                            <In label="Price (€)" type="number" v={form.price} on={(v) => setForm({ ...form, price: v })} testid="ed-price" />
                            <In label="Mileage (km)" type="number" v={form.mileage} on={(v) => setForm({ ...form, mileage: v })} testid="ed-mileage" />
                            <In label="Color" v={form.color} on={(v) => setForm({ ...form, color: v })} testid="ed-color" />
                            <In label="Video URL (YouTube embed)" v={form.video_url} on={(v) => setForm({ ...form, video_url: v })} testid="ed-video" />
                            <In label="Short desc ES" v={form.short_desc_es} on={(v) => setForm({ ...form, short_desc_es: v })} testid="ed-sdes" />
                            <In label="Short desc EN" v={form.short_desc_en} on={(v) => setForm({ ...form, short_desc_en: v })} testid="ed-sden" />
                        </div>

                        <div className="mt-5 grid grid-cols-1 gap-5">
                            <Ta label="Description ES" v={form.description_es} on={(v) => setForm({ ...form, description_es: v })} testid="ed-des" />
                            <Ta label="Description EN" v={form.description_en} on={(v) => setForm({ ...form, description_en: v })} testid="ed-den" />
                        </div>

                        <div className="mt-6">
                            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-2">Images (URLs)</p>
                            {form.images.map((u, i) => (
                                <input
                                    key={i}
                                    type="url"
                                    value={u}
                                    onChange={(e) => {
                                        const arr = [...form.images];
                                        arr[i] = e.target.value;
                                        setForm({ ...form, images: arr });
                                    }}
                                    placeholder="https://…"
                                    data-testid={`ed-img-${i}`}
                                    className="block w-full bg-transparent border-b border-black/20 py-2 text-neutral-900 focus:outline-none focus:border-[--ls-blue] mb-2"
                                />
                            ))}
                            <button
                                onClick={() => setForm({ ...form, images: [...form.images, ""] })}
                                className="font-mono text-xs uppercase tracking-[0.2em] text-neutral-500 hover:text-neutral-900"
                                data-testid="ed-add-img"
                            >
                                + Add image
                            </button>
                        </div>

                        <div className="mt-6">
                            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-2">Specs</p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {Object.keys(EMPTY.specs).map((k) => (
                                    <In
                                        key={k}
                                        label={k.replace(/_/g, " ")}
                                        v={form.specs[k] || ""}
                                        on={(v) => setForm({ ...form, specs: { ...form.specs, [k]: v } })}
                                        testid={`ed-spec-${k}`}
                                    />
                                ))}
                            </div>
                        </div>

                        <label className="mt-6 flex items-center gap-3 text-sm">
                            <input
                                type="checkbox"
                                checked={form.featured}
                                onChange={(e) => setForm({ ...form, featured: e.target.checked })}
                                data-testid="ed-featured"
                            />
                            {t.admin.featured_label}
                        </label>

                        <div className="mt-8 flex justify-end gap-3">
                            <button onClick={() => setEditing(null)} className="btn-outline" data-testid="ed-cancel">
                                {t.admin.cancel}
                            </button>
                            <button onClick={save} disabled={busy} className="btn-primary" data-testid="ed-save">
                                {busy ? "…" : t.admin.save}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function TabBtn({ active, onClick, children, testid }) {
    return (
        <button
            onClick={onClick}
            data-testid={testid}
            className={`inline-flex items-center gap-2 px-5 py-3 text-xs uppercase tracking-[0.2em] border transition-colors ${
                active ? "border-black text-neutral-900 bg-black/5" : "border-black/15 text-neutral-500 hover:text-neutral-900"
            }`}
        >
            {children}
        </button>
    );
}

function In({ label, v, on, type = "text", testid }) {
    return (
        <div className="flex flex-col gap-1">
            <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">{label}</label>
            <input
                type={type}
                value={v}
                onChange={(e) => on(e.target.value)}
                data-testid={testid}
                className="bg-transparent border-b border-black/20 py-2 text-neutral-900 focus:outline-none focus:border-[--ls-blue]"
            />
        </div>
    );
}
function Ta({ label, v, on, testid }) {
    return (
        <div className="flex flex-col gap-1">
            <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">{label}</label>
            <textarea
                rows={3}
                value={v}
                onChange={(e) => on(e.target.value)}
                data-testid={testid}
                className="bg-transparent border-b border-black/20 py-2 text-neutral-900 focus:outline-none focus:border-[--ls-blue]"
            />
        </div>
    );
}
function Sel({ label, v, on, opts, testid }) {
    return (
        <div className="flex flex-col gap-1">
            <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">{label}</label>
            <select
                value={v}
                onChange={(e) => on(e.target.value)}
                data-testid={testid}
                className="bg-transparent border-b border-black/20 py-2 text-neutral-900 focus:outline-none focus:border-[--ls-blue]"
            >
                {opts.map((o) => (
                    <option key={o} value={o} className="bg-[--ls-bg]">{o}</option>
                ))}
            </select>
        </div>
    );
}
